import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import API from "../../api/axiosConfig";
import {
  Card,
  Row,
  Col,
  Tag,
  Table,
  Button,
  Tabs,
  Alert,
  Spin,
  Modal,
  Typography,
  Descriptions,
  Space,
  Divider,
  Tooltip,
  Upload,
  message,
  Badge,
  Select,
  Form,
  DatePicker,
  Input,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  UploadOutlined,
  UserOutlined,
  CarOutlined,
  EnvironmentOutlined,
  FileOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  CreditCardOutlined,
  LinkOutlined,
  SyncOutlined,
  PrinterOutlined,
  EditOutlined,
} from "@ant-design/icons";
import PaymentForm from "../../components/PaymentForm";
import MacaronPaymentForm from "../../components/MacaronPaymentForm";
import DocumentViewer from "../../components/DocumentViewer";
import SubscriptionReceiptPdf from "../../components/SubscriptionReceiptPdf_New";
import {
  approveSubscription,
  cancelSubscription,
  getSubscriptionById,
  processPayment,
  processMacaronPayment,
  rejectSubscription,
  uploadDocument,
  assignVehicleUuidToSubscription,
  renewSubscription,
  changeSectionPermission,
} from "../../api/subscriptions/subscriptionsRequest";
import { getVehicleUuidUnused } from "../../api/vehicle/vehicleUuidRequest";
import { getVehiclesByClientId, changeSubscriptionVehicle } from "../../api/vehicle/vehicleRequest";
import { getMotifsSubscription } from "../../api/motifs-annulation/motifRequests";
import { getClientNames, changeSubscriptionClient } from "../../api/client/clientRequest";
import { BACKEND_URL } from "../../constants";
import {
  CLIENT_TYPES,
  CLIENT_TYPES_COLORS,
  LOCATION_TYPES,
  PAYMENT_DETAIL_TYPES,
  SUBSCRIPTION_STATUS,
} from "../../utils/constants";
import PermissionRender from "../../components/PermissionRender";
import { getPaymentMethodInFrench } from "../../utils/payment";
import SubscriptionReceipt80mm from "../../components/SubscriptionReceipt80mm";
import ChangeRegistrationFeeModal from "../../components/ChangeRegistrationFeeModal";
import ChangeEcheanceAmountModal from "../../components/ChangeEcheanceAmountModal";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { confirm } = Modal;
const { Option } = Select;

// Add these utility functions before the echeancesColumns

const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const calculateDaysOverdue = (paymentDeadline) => {
  const today = new Date();
  const deadline = new Date(paymentDeadline);

  if (today <= deadline) {
    return 0; // Not overdue yet
  }

  const diffTime = today - deadline;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) - 1;
};

const getEcheanceStatusWithOverdue = (echeance) => {
  const daysOverdue = calculateDaysOverdue(echeance.payment_deadline);
  if (echeance.status === "paid") {
    return { status: "paid", daysOverdue: 0, isOverdue: false };
  }

  if (daysOverdue > 0 && echeance.payment_deadline) {
    return { status: "overdue", daysOverdue, isOverdue: true };
  }

  return { status: echeance.status, daysOverdue: 0, isOverdue: false };
};

const getPaymentPeriod = (startDate, paymentDeadline) => {
  return {
    formattedPeriod: `Du ${formatDate(startDate)} au ${formatDate(
      paymentDeadline
    )}`,
  };
};

const SubscriptionView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get active tab from URL params or default to 'client'
  const getActiveTabFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('tab') || 'client';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMacaronPaymentModal, setShowMacaronPaymentModal] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [macaronPaymentSubmitting, setMacaronPaymentSubmitting] = useState(false);
  const [selectedEcheance, setSelectedEcheance] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showReceipt80Modal, setShowReceipt80Modal] = useState(false);

  // Change registration fee modal state
  const [showChangeRegistrationFeeModal, setShowChangeRegistrationFeeModal] = useState(false);

  // Change echeance amount modal state
  const [showChangeEcheanceAmountModal, setShowChangeEcheanceAmountModal] = useState(false);
  const [selectedEcheanceForEdit, setSelectedEcheanceForEdit] = useState(null);

  const [showAssignUuidModal, setShowAssignUuidModal] = useState(false);
  const [availableUuids, setAvailableUuids] = useState([]);
  const [loadingUuids, setLoadingUuids] = useState(false);
  const [assignUuidForm] = Form.useForm();
  const [paymentSelectedId, setPaymentSelectedId] = useState(null);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [cancellationModalForm] = Form.useForm();

  // Change client modal state
  const [showChangeClientModal, setShowChangeClientModal] = useState(false);
  const [clientNames, setClientNames] = useState([]);
  const [loadingClientNames, setLoadingClientNames] = useState(false);
  const [changeClientForm] = Form.useForm();

  // Change vehicle modal state
  const [showChangeVehicleModal, setShowChangeVehicleModal] = useState(false);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [changeVehicleForm] = Form.useForm();

  // Change section permission modal state
  const [showChangeSectionModal, setShowChangeSectionModal] = useState(false);
  const [sectionZone, setSectionZone] = useState(null);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [availableSections, setAvailableSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [changeSectionForm] = Form.useForm();

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await getSubscriptionById(id);
      setSubscription(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to load subscription details");
      console.error(err);
      message.error(
        err?.response?.data?.message || "Erreur de chargement de l'abonnement"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getSubscriptionById(id);
        setSubscription(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to load subscription details");
        console.error(err);
        message.error(
          err?.response?.data?.message || "Erreur de chargement de l'abonnement"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    getMotifsSubscription()
      .then(setCancellationReasons)
      .catch(() => setCancellationReasons([]));
  }, [id]);

  // Handle tab changes and update URL
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('tab', tabKey);
    navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
  };

  // Listen for URL changes to update active tab
  useEffect(() => {
    const newActiveTab = new URLSearchParams(location.search).get('tab') || 'client';
    if (newActiveTab !== activeTab) {
      setActiveTab(newActiveTab);
    }
  }, [location.search, activeTab]);

  const fetchAvailableUuids = async () => {
    setLoadingUuids(true);
    try {
      const data = await getVehicleUuidUnused();
      setAvailableUuids(data || []);
    } catch (err) {
      message.error("Erreur de chargement des UUIDs disponibles");
      console.error("Error fetching available UUIDs:", err);
      setAvailableUuids([]);
    } finally {
      setLoadingUuids(false);
    }
  };

  const handleOpenAssignUuidModal = () => {
    fetchAvailableUuids();
    setShowAssignUuidModal(true);
  };

  const handleAssignUuidSubmit = async (values) => {
    const { selectedUuidData } = values;
    if (!selectedUuidData) {
      message.error("Veuillez sélectionner un UUID.");
      return;
    }

    const [prefix, uuid] = selectedUuidData.split("|");

    if (!prefix || !uuid) {
      message.error("Format UUID invalide sélectionné.");
      console.error("Invalid selected UUID format:", selectedUuidData);
      return;
    }

    setLoading(true);
    try {
      await assignVehicleUuidToSubscription(subscription.id, uuid, prefix);
      message.success("UUID assigné au véhicule avec succès !");
      setShowAssignUuidModal(false);
      assignUuidForm.resetFields();
      await fetchSubscription();
    } catch (err) {
      console.error("Error assigning UUID:", err);
      message.error(
        err?.response?.data?.message || "Échec de l'assignation de l'UUID."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUuidCancel = () => {
    setShowAssignUuidModal(false);
    assignUuidForm.resetFields();
  };

  // Change registration fee handlers
  const handleChangeRegistrationFee = () => {
    setShowChangeRegistrationFeeModal(true);
  };

  const handleChangeRegistrationFeeCancel = () => {
    setShowChangeRegistrationFeeModal(false);
  };

  const handleChangeRegistrationFeeSuccess = () => {
    setShowChangeRegistrationFeeModal(false);
    // Refresh the subscription data
    fetchSubscription();
  };

  // Change echeance amount handlers
  const handleChangeEcheanceAmount = (echeance) => {
    setSelectedEcheanceForEdit(echeance);
    setShowChangeEcheanceAmountModal(true);
  };

  const handleChangeEcheanceAmountCancel = () => {
    setShowChangeEcheanceAmountModal(false);
    setSelectedEcheanceForEdit(null);
  };

  const handleChangeEcheanceAmountSuccess = () => {
    setShowChangeEcheanceAmountModal(false);
    setSelectedEcheanceForEdit(null);
    // Refresh the subscription data
    fetchSubscription();
  };

  // Change client handlers
  const handleOpenChangeClientModal = async () => {
    setShowChangeClientModal(true);
    setLoadingClientNames(true);
    try {
      const data = await getClientNames();
      setClientNames(data);
    } catch (error) {
      message.error("Erreur lors du chargement des clients");
      console.error(error);
    } finally {
      setLoadingClientNames(false);
    }
  };

  const handleChangeClientSubmit = async (values) => {
    try {
      await changeSubscriptionClient(id, values.client_id, values.reason);
      message.success("Client modifié avec succès");
      setShowChangeClientModal(false);
      changeClientForm.resetFields();
      await fetchSubscription();
    } catch (error) {
      message.error(error?.response?.data?.message || "Erreur lors du changement de client");
      console.error(error);
    }
  };

  const handleChangeClientCancel = () => {
    setShowChangeClientModal(false);
    changeClientForm.resetFields();
  };

  // Change vehicle handlers
  const handleOpenChangeVehicleModal = async () => {
    if (!subscription?.client?.id) {
      message.error("Aucun client associé à cet abonnement");
      return;
    }
    setShowChangeVehicleModal(true);
    setLoadingVehicles(true);
    try {
      const data = await getVehiclesByClientId(subscription.client.id);
      setAvailableVehicles(data);
    } catch (error) {
      message.error("Erreur lors du chargement des véhicules");
      console.error(error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleChangeVehicleSubmit = async (values) => {
    try {
      await changeSubscriptionVehicle(id, values.vehicle_id, values.reason);
      message.success("Véhicule modifié avec succès");
      setShowChangeVehicleModal(false);
      changeVehicleForm.resetFields();
      await fetchSubscription();
    } catch (error) {
      message.error(error?.response?.data?.message || "Erreur lors du changement de véhicule");
      console.error(error);
    }
  };

  const handleChangeVehicleCancel = () => {
    setShowChangeVehicleModal(false);
    changeVehicleForm.resetFields();
  };

  // Change section permission handlers
  const handleOpenChangeSectionModal = async () => {
    // Find current section permission
    const currentSection = subscription.permissions?.find(p => p.entity_type === 'section');
    if (!currentSection) {
      message.error("Aucune permission de section trouvée pour cet abonnement");
      return;
    }
    
    setCurrentSectionId(currentSection.entity_id);
    setShowChangeSectionModal(true);
    setLoadingSections(true);
    try {
      const response = await API.get(`/subscriptions/${id}/sections-by-zone`);
      setSectionZone(response.data.zone);
      setAvailableSections(response.data.sections);
    } catch (error) {
      message.error("Erreur lors du chargement des sections");
      console.error(error);
    } finally {
      setLoadingSections(false);
    }
  };

  const handleChangeSectionSubmit = async (values) => {
    try {
      const response = await changeSectionPermission(id, currentSectionId, values.new_entity_id, values.reason);
      message.success(response.message || "Permission modifiée avec succès");
      setShowChangeSectionModal(false);
      changeSectionForm.resetFields();
      await fetchSubscription();
    } catch (error) {
      message.error(error?.response?.data?.message || "Erreur lors du changement de permission");
      console.error(error);
    }
  };

  const handleChangeSectionCancel = () => {
    setShowChangeSectionModal(false);
    changeSectionForm.resetFields();
  };

  const handleApprove = () => {
    confirm({
      title: "Êtes-vous sûr de vouloir approuver cet abonnement?",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      okText: "Approuver",
      okType: "primary",
      cancelText: "Annuler",
      async onOk() {
        try {
          await approveSubscription(id);
          await fetchSubscription();
          message.success("Abonnement approuvé avec succès");
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Échec de l'approbation"
          );
          console.error(err);
        }
      },
    });
  };

  const handleReject = () => {
    confirm({
      title: "Rejeter l'abonnement",
      icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div className="mt-4">
          <textarea
            id="rejection_reason"
            className="w-full p-2 border rounded"
            placeholder="Motif du rejet"
            rows={4}
          />
        </div>
      ),
      okText: "Rejeter",
      okType: "danger",
      cancelText: "Annuler",
      async onOk() {
        const reason = document.getElementById("rejection_reason").value;
        if (!reason.trim()) {
          message.error("Le motif de rejet est obligatoire");
          return Promise.reject("Motif obligatoire");
        }
        try {
          await rejectSubscription(id, reason);
          await fetchSubscription();
          message.success("Abonnement rejeté");
        } catch (err) {
          message.error(err?.response?.data?.message || "Échec du rejet");
          console.error(err);
          return Promise.reject(err);
        }
      },
    });
  };

  const handleCancel = () => {
    confirm({
      title: "Résilier l'abonnement",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <Form form={cancellationModalForm} layout="vertical">
          <Form.Item
            name="cancellation_reason_id"
            label="Motif de résiliation"
            rules={[
              { required: true, message: "Veuillez sélectionner un motif" },
            ]}
          >
            <Select placeholder="Sélectionner un motif">
              {cancellationReasons.map((reason) => (
                <Option key={reason.id} value={reason.id}>
                  {reason.label || reason.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="cancellation_reason"
            label="Détail du motif"
            rules={[{ required: true, message: "Le motif est obligatoire" }]}
          >
            <textarea
              className="w-full p-2 border rounded"
              placeholder="Motif de résiliation"
              rows={4}
            />
          </Form.Item>
          <Form.Item
            name="cancelled_at"
            label="Date de résiliation"
            rules={[{ required: true, message: "La date est obligatoire" }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              className="w-full"
            />
          </Form.Item>
        </Form>
      ),
      okText: "Résilier l'abonnement",
      okType: "danger",
      cancelText: "Retour",
      async onOk() {
        try {
          const values = await cancellationModalForm.validateFields();
          await cancelSubscription(id, values);
          await fetchSubscription();
          message.success("Abonnement annulé avec succès");
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Échec de l'annulation"
          );
          console.error(err);
          return Promise.reject(err);
        } finally {
          cancellationModalForm.resetFields();
        }
      },
      onCancel() {
        cancellationModalForm.resetFields();
      },
    });
  };

  const handleRenew = () => {
    confirm({
      title: "Êtes-vous sûr de vouloir renouveler cet abonnement ?",
      icon: <SyncOutlined style={{ color: "#1890ff" }} />,
      content:
        "Cela prolongera la date de fin et générera de nouvelles échéances.",
      okText: "Renouveler",
      okType: "primary",
      cancelText: "Annuler",
      async onOk() {
        setLoading(true);
        try {
          await renewSubscription(id);
          await fetchSubscription();
          message.success("Abonnement renouvelé avec succès !");
        } catch (err) {
          console.error("Error renewing subscription:", err);
          message.error(
            err?.response?.data?.message ||
              "Échec du renouvellement de l'abonnement."
          );
        } finally {
          setLoading(false);
        }
      },
      onCancel() {
        console.log("Renewal cancelled");
      },
    });
  };

  const handlePaymentClick = (echeance) => {
    setSelectedEcheance(echeance);
    setShowPaymentModal(true);
  };
  const handlePaymentSubmit = async (values) => {
    setPaymentSubmitting(true);
    try {
      await processPayment({
        subscription_id: subscription.id,
        echeance_id: selectedEcheance.id,
        ...values,
      });

      setShowPaymentModal(false);

      await fetchSubscription();
      message.success("Paiement effectué avec succès");
    } catch (err) {
      message.error(
        err?.response?.data?.error || "Le traitement du paiement a échoué"
      );
      console.error(err?.response?.data?.error);
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleMacaronPaymentClick = () => {
    setShowMacaronPaymentModal(true);
  };

  const handleMacaronPaymentSubmit = async (values) => {
    setMacaronPaymentSubmitting(true);
    try {
      await processMacaronPayment(values);

      setShowMacaronPaymentModal(false);

      await fetchSubscription();
      message.success("Paiement des frais de macaron effectué avec succès");
    } catch (err) {
      message.error(
        err?.response?.data?.error || "Le traitement du paiement des frais de macaron a échoué"
      );
      console.error(err?.response?.data?.error);
    } finally {
      setMacaronPaymentSubmitting(false);
    }
  };

  const handlePrintReceipt = (id) => {
    setShowReceiptModal(true);
    setPaymentSelectedId(id);
  };
  const handlePrintReceipt80 = (id) => {
    setShowReceipt80Modal(true);
    setPaymentSelectedId(id);
  };

  const handleViewDocument = (documentPath) => {
    setDocumentUrl(`${BACKEND_URL}/subscriptions/${documentPath}`);
    setShowDocumentModal(true);
  };

  const handleUploadDocument = async (options) => {
    const { file, onSuccess, onError } = options;

    const formData = new FormData();
    formData.append("document", file);

    try {
      const response = await uploadDocument(id, formData);
      onSuccess(response, file);

      await fetchSubscription();
      message.success(`${file.name} téléchargé avec succès`);
    } catch (err) {
      onError(err);
      message.error(
        err?.response?.data?.message ||
          `Échec du téléchargement de ${file.name}`
      );
    }
  };

  const handleViewEntity = (record) => {
    const { entity_type, entity } = record;
    let url = "";

    switch (entity_type) {
      case "zone":
        url = `/zones/${entity.id}/sections`;
        break;
      case "section":
        url = `/sections/${entity.id}/streets`;
        break;
      case "street":
        url = `/streets/${entity.id}/lines`;
        break;
      case "line":
        url = `/streets/${entity.street_id}/lines`;
        break;
      default:
        message.warning("Type d'entité non supporté pour la visualisation");
        return;
    }

    // Open in new window
    window.open(url, "_blank");
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "";

    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusTag = (status) => {
    const statusKey = Object.keys(SUBSCRIPTION_STATUS).find(
      (key) => key === status
    );
    const statusValue = SUBSCRIPTION_STATUS[statusKey] || status;

    switch (status) {
      case "pending":
        return <Tag color="warning">{statusValue}</Tag>;
      case "active":
        return <Tag color="success">{statusValue}</Tag>;
      case "rejected":
        return <Tag color="error">{statusValue}</Tag>;
      case "canceled":
        return <Tag color="default">{statusValue}</Tag>;
      case "expired":
        return <Tag color="default">{statusValue}</Tag>;
      default:
        return <Tag color="blue">{statusValue}</Tag>;
    }
  };

  const getEcheanceStatusTag = (status) => {
    switch (status) {
      case "pending":
        return <Tag color="warning">En attente</Tag>;
      case "paid":
        return <Tag color="success">Payé</Tag>;
      case "overdue":
        return <Tag color="error">En retard</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const getClientDisplayName = (client) => {
    if (!client) return "N/A";
    if (client.type === "individuals") {
      return (
        `${client.civility || ""} ${client.first_name || ""} ${
          client.last_name || ""
        }`.trim() || "N/A"
      );
    }
    return client.name || "N/A";
  };

  const echeancesColumns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Période",
      key: "period",
      render: (record) => {
        if (record.payment_deadline) {
          const statusInfo = getEcheanceStatusWithOverdue(record);
          const paymentPeriod = getPaymentPeriod(
            record.start_date,
            record.payment_deadline
          );

          return (
            <div>
              <div className="font-medium">
                {`${formatDate(record.start_date)} - ${formatDate(
                  record.end_date
                )}`}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Paiement: {paymentPeriod.formattedPeriod}
              </div>
              {statusInfo.isOverdue && (
                <div className="text-red-500 text-xs mt-1 font-medium">
                  <ExclamationCircleOutlined className="mr-1" />
                  En retard de {statusInfo.daysOverdue} jour
                  {statusInfo.daysOverdue > 1 ? "s" : ""}
                </div>
              )}
            </div>
          );
        } else {
          return (
            <div className="font-medium">
              {`${formatDate(record.start_date)} - ${formatDate(
                record.end_date
              )}`}
            </div>
          );
        }
      },
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatCurrency(amount),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const statusInfo = getEcheanceStatusWithOverdue(record);

        if (statusInfo.isOverdue) {
          return (
            <div className="space-y-1">
              <Tag color="error" icon={<ExclamationCircleOutlined />}>
                En retard
              </Tag>
            </div>
          );
        }

        return getEcheanceStatusTag(status);
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record, index) => {
        if (record.status === "pending" || record.status === "partial") {
          let isDisabled = true;
          if (subscription && subscription.echeances) {
            const firstUnpaidIndex = subscription.echeances
              .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
              .findIndex(
                (e) => e.status === "pending" || e.status === "partial"
              );
            isDisabled = index !== firstUnpaidIndex;
          }
          return (
            <Space>
              <PermissionRender requiredPermission={["subscriptions", "pay"]}>
                <Button
                  type="primary"
                  size="small"
                  icon={<DollarOutlined />}
                  onClick={() => handlePaymentClick(record)}
                  disabled={isDisabled}
                >
                  Payer
                </Button>
              </PermissionRender>
              <PermissionRender requiredPermission={["subscriptions", "update"]}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleChangeEcheanceAmount(record)}
                >
                  Modifier montant
                </Button>
              </PermissionRender>
            </Space>
          );
        } else if (record.status === "paid") {
          return (
            <Button
              size="small"
              type="text"
              icon={<CheckCircleOutlined className="text-green-500" />}
              disabled
            >
              Payé
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const permissionsColumns = [
    {
      title: "#",
      key: "index",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Type d'entité",
      dataIndex: "entity_type",
      key: "entity_type",
      render: (type) => LOCATION_TYPES[type] || type,
    },
    {
      title: "Nom de l'entité",
      dataIndex: ["entity", "name"],
      key: "entity_id",
    },
    {
      title: "Type de permission",
      dataIndex: "permission_type",
      key: "permission_type",
      render: (type) => {
        if (type === "allow") {
          return <Tag color="success">Autorisé</Tag>;
        } else if (type === "deny") {
          return <Tag color="error">Refusé</Tag>;
        }
        return type;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          {["zone", "section", "street", "line"].includes(record.entity_type) && (
            <PermissionRender requiredPermission={["zone", "menu"]}>
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewEntity(record)}
              >
                Voir
              </Button>
            </PermissionRender>
          )}
          {record.entity_type === "section" && subscription.status === "active" && (
            <PermissionRender requiredPermission={["subscriptions", "update_section"]}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={handleOpenChangeSectionModal}
              >
                Changer
              </Button>
            </PermissionRender>
          )}
        </Space>
      ),
    },
  ];

  const paymentsColumns = [
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: "Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (date) => formatDateTime(date),
    },
    {
      title: "Méthode",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (method) => getPaymentMethodInFrench(method),
    },
    {
      title: "Référence de paiement",
      dataIndex: "reference_number",
      key: "reference_number",
    },
    {
      title: "Montant total",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatCurrency(amount),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (notes) => notes || "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          <Tooltip title="Imprimer le reçu" className="flex gap-4">
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => handlePrintReceipt(record.id)}
            >
              A4
            </Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => handlePrintReceipt80(record.id)}
            >
              80mm
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const paymentDetailsColumns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => PAYMENT_DETAIL_TYPES[type] || type,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatCurrency(amount),
    },
  ];

  const expandedRowRender = (record) => {
    return (
      <div className="px-4 py-2">
        <Title level={5} className="mb-3">
          Détails du paiement
        </Title>
        <Table
          columns={paymentDetailsColumns}
          dataSource={record.details || []}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </div>
    );
  };

  if (loading && !showAssignUuidModal) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" tip="Chargement..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Erreur"
        description={error}
        type="error"
        showIcon
        className="m-4"
      />
    );
  }

  if (!subscription) {
    return (
      <Alert
        message="Abonnement non trouvé"
        description="L'abonnement demandé n'existe pas."
        type="warning"
        showIcon
        className="m-4"
      />
    );
  }

  const client = subscription.client;
  const hasPayment = subscription.payments && subscription.payments.length > 0;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <Title level={2} className="mb-4 md:mb-0">
          Détails de l'Abonnement
        </Title>
        <Space wrap>
          {subscription.status === "active" && (
            <PermissionRender requiredPermission={["vehicles", "update"]}>
              <Button
                icon={<LinkOutlined />}
                onClick={handleOpenAssignUuidModal}
                disabled={!subscription.vehicle || loading}
              >
                Assigner UUID Véhicule
              </Button>
            </PermissionRender>
          )}
          {subscription.status === "active" && !hasPayment && (
            <PermissionRender requiredPermission={["subscriptions", "update"]}>
              <Button
                icon={<EditOutlined />}
                onClick={handleChangeRegistrationFee}
                disabled={loading}
              >
                Modifier frais d'inscription
              </Button>
            </PermissionRender>
          )}
          {subscription.status === "active" && (
            <PermissionRender requiredPermission={["subscriptions", "renew"]}>
              <Button
                icon={<SyncOutlined />}
                onClick={handleRenew}
                disabled={loading}
                type="default"
                style={{ borderColor: "#1890ff", color: "#1890ff" }}
              >
                Renouveler l'abonnement
              </Button>
            </PermissionRender>
          )}
          {subscription.status === "active" && (
            <PermissionRender requiredPermission={["subscriptions", "cancel"]}>
              <Button danger onClick={handleCancel} disabled={loading}>
                Résilier l'abonnement
              </Button>
            </PermissionRender>
          )}
          {subscription.status === "pending" && (
            <>
              <PermissionRender
                requiredPermission={["subscriptions", "approve"]}
              >
                <Button
                  type="primary"
                  onClick={handleApprove}
                  disabled={loading}
                >
                  <CheckCircleOutlined /> Approuver
                </Button>
              </PermissionRender>
              <PermissionRender
                requiredPermission={["subscriptions", "reject"]}
              >
                <Button danger onClick={handleReject} disabled={loading}>
                  <CloseCircleOutlined /> Rejeter
                </Button>
              </PermissionRender>
            </>
          )}
        </Space>
      </div>

      {subscription.vehicle && subscription.vehicle.owner_id !== subscription.client?.id && (
        <Alert
          message="Ce véhicule n'appartient pas au client de cet abonnement."
          type="warning"
          showIcon
          className="mb-4"
          banner
        />
      )}

      <Spin spinning={loading} tip="Traitement...">
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
            <Title level={4} className="mb-2 md:mb-0">
              Abonnement #{subscription.contract_number || "N/A"}
            </Title>
            <div>{getStatusTag(subscription.status)}</div>
          </div>

          <Divider className="my-4" />

          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Title level={5} className="mb-4">
                Informations générales
              </Title>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Type d'abonnement">
                  {subscription.subscription_type?.name || "N/A"} ({" "}
                  {subscription?.subscription_audience?.name || "N/A"} )
                </Descriptions.Item>
                <Descriptions.Item label="Date de début">
                  {formatDate(subscription.start_date)}
                </Descriptions.Item>
                <Descriptions.Item label="Date de fin">
                  {formatDate(subscription.end_date)}
                </Descriptions.Item>
                {subscription.vehicle?.prefix && subscription.vehicle?.uuid && (
                  <Descriptions.Item label="UUID Véhicule">
                    <Tag>{subscription.vehicle.prefix}</Tag>{" "}
                    {subscription.vehicle.uuid}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Frais d'inscription">
                  <Space>
                    {formatCurrency(subscription.registration_fee)}
                    {subscription.registration_fee_paid ? (
                      <Badge
                        status="success"
                        text={<Text type="success">Payé</Text>}
                      />
                    ) : (
                      <Badge
                        status="warning"
                        text={<Text type="warning">Non payé</Text>}
                      />
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Date limite de soumission">
                  {formatDate(subscription.submission_deadline)}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} className="mb-4">
                Informations d'approbation
              </Title>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Créé par">
                  {subscription.creator?.full_name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Date de création">
                  {formatDate(subscription.created_at)}
                </Descriptions.Item>
                <Descriptions.Item label="Approuvé par">
                  {subscription.approver?.full_name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Date d'approbation">
                  {formatDate(subscription.approved_at) || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Notes">
                  {subscription.notes || "Aucune note"}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>

        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange} 
          className="subscription-tabs"
        >
          <TabPane
            tab={
              <span>
                <UserOutlined /> Client
              </span>
            }
            key="client"
          >
            <Card>
              <div className="flex justify-between items-center mb-4">
                <Title level={5} className="mb-0">
                  Informations du client
                </Title>
                {subscription.status === "active" && (
                  <PermissionRender requiredPermission={["subscriptions", "update_client"]}>
                    <Button
                      icon={<EditOutlined />}
                      onClick={handleOpenChangeClientModal}
                      size="small"
                    >
                      Changer le client
                    </Button>
                  </PermissionRender>
                )}
              </div>
              {client ? (
                <Descriptions
                  bordered
                  layout="vertical"
                  column={{ xs: 1, sm: 2, md: 3 }}
                >
                  <Descriptions.Item label="Nom Complet / Raison Sociale">
                    {getClientDisplayName(client)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Type de client">
                    <Tag color={CLIENT_TYPES_COLORS[client?.type] || "default"}>
                      {CLIENT_TYPES[client?.type] || "N/A"}
                    </Tag>
                  </Descriptions.Item>
                  {client.type === "individuals" && (
                    <Descriptions.Item label="CIN">
                      {client.cin || "N/A"}
                    </Descriptions.Item>
                  )}
                  {client.type === "companies" && (
                    <>
                      <Descriptions.Item label="ICE">
                        {client.ice || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="RC">
                        {client.rc || "N/A"}
                      </Descriptions.Item>{" "}
                      {/* Add RC */}
                      <Descriptions.Item label="Prénom Contact">
                        {client.contact_first_name || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Nom Contact">
                        {client.contact_last_name || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Téléphone Contact">
                        {client.contact_phone || "N/A"}
                      </Descriptions.Item>
                    </>
                  )}
                  {client.type === "institutions" && (
                    <>
                      {client.public_institution ? (
                        <Descriptions.Item label="Institution publique">
                          {client.public_institution.name}
                        </Descriptions.Item>
                      ) : (
                        <Descriptions.Item label="Institution publique">
                          N/A
                        </Descriptions.Item>
                      )}
                    </>
                  )}
                  <Descriptions.Item label="Email">
                    {client.email || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Téléphone Principal">
                    {client.phone || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Adresse Ligne 1">
                    {client.address || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Adresse Ligne 2">
                    {client.address2 || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ville">
                    {client.city || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Code Postal">
                    {client.postal_code || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pays">
                    {client.country || "N/A"}
                  </Descriptions.Item>
                  {client.id && (
                    <Descriptions.Item label="Actions">
                      <Button
                        type="link"
                        href={`/client/${client.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Plus de détails
                      </Button>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ) : (
                <Alert
                  message="Aucune information client disponible."
                  type="warning"
                  showIcon
                />
              )}
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <CarOutlined /> Véhicule
              </span>
            }
            key="vehicle"
          >
            <Card>
              <div className="flex justify-between items-center mb-4">
                <Title level={5} className="mb-0">
                  Informations du véhicule
                </Title>
                {subscription.status === "active" && (
                  <PermissionRender requiredPermission={["subscriptions", "update_vehicle"]}>
                    <Button
                      icon={<EditOutlined />}
                      onClick={handleOpenChangeVehicleModal}
                      size="small"
                    >
                      Changer le véhicule
                    </Button>
                  </PermissionRender>
                )}
              </div>
              {subscription.vehicle ? (
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={12}>
                      <Descriptions column={1} bordered size="small">
                      <Descriptions.Item label="Matricule">
                        {subscription.vehicle.matricule}
                      </Descriptions.Item>
                      <Descriptions.Item label="Couleur">
                        {subscription.vehicle.color || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="UUID Assigné">
                        {subscription.vehicle.prefix &&
                        subscription.vehicle.uuid ? (
                          <>
                            <Tag>{subscription.vehicle.prefix}</Tag>{" "}
                            {subscription.vehicle.uuid}
                          </>
                        ) : (
                          <Text type="secondary">Non assigné</Text>
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Descriptions column={1} bordered size="small">
                      <Descriptions.Item label="Marque">
                        {subscription.vehicle.brand || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Modèle">
                        {subscription.vehicle.model || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Actions">
                        <Button
                          type="link"
                          href={`/vehicle/${subscription.vehicle.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Plus de détails
                        </Button>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                </Row>
              ) : (
                <Alert
                  message="Aucun véhicule associé à cet abonnement."
                  type="info"
                  showIcon
                />
              )}
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <DollarOutlined /> Échéances
              </span>
            }
            key="echeances"
          >
            <Card>
              <Title level={5} className="mb-4">
                Échéances de paiement
              </Title>
              <Table
                dataSource={
                  subscription.echeances.sort(
                    (a, b) => new Date(a.start_date) - new Date(b.start_date)
                  ) || []
                }
                columns={echeancesColumns}
                rowKey="id"
                pagination={false}
                bordered
              />
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <CreditCardOutlined /> Paiements
              </span>
            }
            key="payments"
          >
            <Card>
              <div className="flex justify-between items-center mb-4">
                <Title level={5} className="mb-0">
                  Historique des paiements
                </Title>
                <PermissionRender
                  requiredPermission={["subscriptions", "pay"]}
                >
                  <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    onClick={handleMacaronPaymentClick}
                    disabled={subscription.status !== 'active' || !subscription.vehicle}
                  >
                    Frais de macaron
                  </Button>
                </PermissionRender>
              </div>
              {subscription.payments && subscription.payments.length > 0 ? (
                <Table
                  dataSource={
                    subscription.payments.sort((a, b) => b.id - a.id) || []
                  }
                  columns={paymentsColumns}
                  rowKey="id"
                  pagination={false}
                  bordered
                  expandable={{
                    expandedRowRender,
                    expandRowByClick: true,
                  }}
                />
              ) : (
                <Alert
                  message="Aucun paiement enregistré"
                  type="info"
                  showIcon
                />
              )}
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <EnvironmentOutlined /> Permissions
              </span>
            }
            key="permissions"
          >
            <Card>
              <Title level={5} className="mb-4">
                Permissions de stationnement
              </Title>
              <Table
                dataSource={subscription.permissions || []}
                columns={permissionsColumns}
                rowKey="id"
                pagination={false}
                bordered
              />
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <FileOutlined /> Documents
              </span>
            }
            key="documents"
          >
            <Card>
              <Title level={5} className="mb-4">
                Documents
              </Title>
              <div className="mb-4 flex justify-end">
                <Upload
                  customRequest={handleUploadDocument}
                  showUploadList={false}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                >
                  <PermissionRender
                    requiredPermission={["subscriptions", "update"]}
                  >
                    <Button icon={<UploadOutlined />} type="primary">
                      Télécharger document
                    </Button>
                  </PermissionRender>
                </Upload>
              </div>

              <div className="document-list">
                {subscription.documents ? (
                  <div className="space-y-2">
                    {subscription.documents
                      .split(",")
                      .map((document, index) => (
                        <Card
                          key={index}
                          size="small"
                          className="hover:shadow-md transition-shadow duration-300"
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center">
                              <FileOutlined className="mr-2" />
                              <Text>Document {index + 1}</Text>
                            </div>
                            <Space>
                              <Tooltip title="Visualiser">
                                <Button
                                  icon={<EyeOutlined />}
                                  onClick={() => handleViewDocument(document)}
                                />
                              </Tooltip>
                              <Tooltip title="Télécharger">
                                <Button
                                  icon={<DownloadOutlined />}
                                  href={`${BACKEND_URL}/subscriptions/${document}`}
                                  download
                                />
                              </Tooltip>
                            </Space>
                          </div>
                        </Card>
                      ))}
                  </div>
                ) : (
                  <Alert
                    message="Aucun document disponible"
                    type="info"
                    showIcon
                  />
                )}
              </div>
            </Card>
          </TabPane>
        </Tabs>
      </Spin>

      <Modal
        title="Traiter le paiement"
        open={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
        destroyOnClose
      >
        {selectedEcheance && (
          <PaymentForm
            echeance={selectedEcheance}
            onFinish={handlePaymentSubmit}
            onCancel={() => setShowPaymentModal(false)}
            loading={paymentSubmitting}
          />
        )}
      </Modal>

      <Modal
        title="Frais de changement de macaron"
        open={showMacaronPaymentModal}
        onCancel={() => setShowMacaronPaymentModal(false)}
        footer={null}
        destroyOnClose
      >
        {subscription && (
          <MacaronPaymentForm
            subscription={subscription}
            onFinish={handleMacaronPaymentSubmit}
            onCancel={() => setShowMacaronPaymentModal(false)}
            loading={macaronPaymentSubmitting}
          />
        )}
      </Modal>

      <Modal
        title="Visualisation du document"
        open={showDocumentModal}
        onCancel={() => setShowDocumentModal(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setShowDocumentModal(false)}>
            Fermer
          </Button>,
          <Button key="download" type="primary" href={documentUrl} download>
            Télécharger
          </Button>,
        ]}
      >
        <DocumentViewer documentUrl={documentUrl} />
      </Modal>

      <Modal
        title="Assigner un UUID au Véhicule"
        open={showAssignUuidModal}
        onCancel={handleAssignUuidCancel}
        confirmLoading={loading}
        destroyOnClose
        footer={[
          <Button
            key="back"
            onClick={handleAssignUuidCancel}
            disabled={loading}
          >
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => assignUuidForm.submit()}
          >
            Assigner
          </Button>,
        ]}
      >
        <Spin spinning={loadingUuids} tip="Chargement des UUIDs...">
          <Form
            form={assignUuidForm}
            layout="vertical"
            onFinish={handleAssignUuidSubmit}
            initialValues={{ selectedUuidData: null }}
          >
            <Form.Item
              name="selectedUuidData"
              label="Sélectionner UUID (Préfixe | UUID)"
              rules={[
                { required: true, message: "Veuillez sélectionner un UUID !" },
              ]}
            >
              <Select
                showSearch
                placeholder="Choisir un UUID disponible"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                disabled={loadingUuids || availableUuids.length === 0}
              >
                {availableUuids.map((item) => (
                  <Option
                    key={`${item.prefix}|${item.uuid}`}
                    value={`${item.prefix}|${item.uuid}`}
                  >
                    {`${item.prefix} | ${item.uuid}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {availableUuids.length === 0 && !loadingUuids && (
              <Alert message="Aucun UUID disponible." type="warning" showIcon />
            )}{" "}
          </Form>
        </Spin>
      </Modal>

      {paymentSelectedId && showReceiptModal && (
        <SubscriptionReceiptPdf
          subscriptionData={subscription}
          visible={showReceiptModal}
          onClose={() => setShowReceiptModal(false)}
          paymentId={paymentSelectedId}
        />
      )}
      {paymentSelectedId && showReceipt80Modal && (
        <SubscriptionReceipt80mm
          subscriptionData={subscription}
          visible={showReceipt80Modal}
          onClose={() => setShowReceipt80Modal(false)}
          paymentId={paymentSelectedId}
        />
      )}

      <ChangeRegistrationFeeModal
        visible={showChangeRegistrationFeeModal}
        onCancel={handleChangeRegistrationFeeCancel}
        onSuccess={handleChangeRegistrationFeeSuccess}
        subscription={subscription}
      />

      <ChangeEcheanceAmountModal
        visible={showChangeEcheanceAmountModal}
        onCancel={handleChangeEcheanceAmountCancel}
        onSuccess={handleChangeEcheanceAmountSuccess}
        echeance={selectedEcheanceForEdit}
      />

      {/* Change Client Modal */}
      <Modal
        title="Changer le client"
        open={showChangeClientModal}
        onCancel={handleChangeClientCancel}
        footer={[
          <Button key="back" onClick={handleChangeClientCancel}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => changeClientForm.submit()}
          >
            Confirmer
          </Button>,
        ]}
        destroyOnClose
      >
        <Spin spinning={loadingClientNames} tip="Chargement des clients...">
          <Form
            form={changeClientForm}
            layout="vertical"
            onFinish={handleChangeClientSubmit}
          >
            <Form.Item
              name="client_id"
              label="Sélectionner un nouveau client"
              rules={[
                { required: true, message: "Veuillez sélectionner un client !" },
              ]}
            >
              <Select
                showSearch
                placeholder="Choisir un client"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                disabled={loadingClientNames}
              >
                {clientNames.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.display_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="reason"
              label="Raison du changement"
            >
              <Input.TextArea
                placeholder="Entrez la raison du changement de client"
                rows={3}
              />
            </Form.Item>
            {clientNames.length === 0 && !loadingClientNames && (
              <Alert message="Aucun client disponible." type="warning" showIcon />
            )}
          </Form>
        </Spin>
      </Modal>

      {/* Change Vehicle Modal */}
      <Modal
        title="Changer le véhicule"
        open={showChangeVehicleModal}
        onCancel={handleChangeVehicleCancel}
        footer={[
          <Button key="back" onClick={handleChangeVehicleCancel}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => changeVehicleForm.submit()}
          >
            Confirmer
          </Button>,
        ]}
        destroyOnClose
      >
        <Spin spinning={loadingVehicles} tip="Chargement des véhicules...">
          <Form
            form={changeVehicleForm}
            layout="vertical"
            onFinish={handleChangeVehicleSubmit}
          >
            <Form.Item
              name="vehicle_id"
              label="Sélectionner un nouveau véhicule"
              rules={[
                { required: true, message: "Veuillez sélectionner un véhicule !" },
              ]}
            >
              <Select
                showSearch
                placeholder="Choisir un véhicule"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                disabled={loadingVehicles}
              >
                {availableVehicles.map((vehicle) => (
                  <Option key={vehicle.id} value={vehicle.id}>
                    {vehicle.matricule} - {vehicle.brand || "N/A"} {vehicle.model || ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="reason"
              label="Raison du changement"
            >
              <Input.TextArea
                placeholder="Entrez la raison du changement de véhicule"
                rows={3}
              />
            </Form.Item>
            {availableVehicles.length === 0 && !loadingVehicles && (
              <Alert message="Aucun véhicule disponible pour ce client." type="warning" showIcon />
            )}
          </Form>
        </Spin>
      </Modal>

      {/* Change Section Permission Modal */}
      <Modal
        title="Changer la zone autorisée"
        open={showChangeSectionModal}
        onCancel={handleChangeSectionCancel}
        footer={[
          <Button key="back" onClick={handleChangeSectionCancel}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => changeSectionForm.submit()}
          >
            Confirmer
          </Button>,
        ]}
        destroyOnClose
      >
        <Spin spinning={loadingSections} tip="Chargement des sections...">
          {sectionZone && (
            <Alert
              message={`Contrat: ${sectionZone.name}`}
              type="info"
              showIcon
              className="mb-4"
            />
          )}
          {currentSectionId && (
            <Alert
              message={`Zone actuelle: ${availableSections.find(s => s.id === currentSectionId)?.name || 'Chargement...'}`}
              type="warning"
              showIcon
              className="mb-4"
            />
          )}
          <Form
            form={changeSectionForm}
            layout="vertical"
            onFinish={handleChangeSectionSubmit}
          >
            <Form.Item
              name="new_entity_id"
              label="Sélectionner une nouvelle zone"
              rules={[
                { required: true, message: "Veuillez sélectionner une zone !" },
              ]}
            >
              <Select
                showSearch
                placeholder="Choisir une zone"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                disabled={loadingSections}
              >
                {availableSections.map((section) => (
                  <Option key={section.id} value={section.id}>
                    {section.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="reason"
              label="Raison du changement (optionnel)"
            >
              <Input.TextArea
                placeholder="Entrez la raison du changement de zone"
                rows={3}
              />
            </Form.Item>
            {availableSections.length === 0 && !loadingSections && (
              <Alert message="Aucune zone disponible." type="warning" showIcon />
            )}
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default SubscriptionView;
